import * as React from "react";
import { formatRelativeTime } from "@/lib/relativeTime";
import { GraduationCap, RefreshCw } from "lucide-react";
import {
  Button,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTablePagination,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from "@eduviaos/ui-kit";
import { usePageSizePreference } from "@/hooks/usePageSizePreference";
import { useStudentsLearningSummary } from "@/hooks/useStudentsLearning";

export function LearningProgressTab() {
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = usePageSizePreference({
    scope: "learning-progress",
    defaultPageSize: 25,
  });
  const filters = React.useMemo(
    () => ({ limit: pageSize, offset: page * pageSize }),
    [page, pageSize],
  );
  const query = useStudentsLearningSummary(filters, true);
  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  return (
    <DataTable>
      <DataTableToolbar>
        <DataTableToolbarSpacer />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => query.refetch()}
          disabled={query.isFetching}
        >
          <RefreshCw
            aria-hidden="true"
            className={cn("size-4", query.isFetching && "animate-spin")}
          />
          Refresh
        </Button>
        <span className="text-muted-foreground text-xs tabular-nums">
          {total.toLocaleString()} learners
        </span>
      </DataTableToolbar>
      <DataTableScroll>
        <Table>
          <caption className="sr-only">Learner learning progress</caption>
          <TableHeader>
            <TableRow>
              <TableHead>Learner</TableHead>
              <TableHead className="text-right">Plans</TableHead>
              <TableHead className="text-right">Due</TableHead>
              <TableHead className="text-right">Streak</TableHead>
              <TableHead>Last active</TableHead>
            </TableRow>
          </TableHeader>
          {query.isLoading ? (
            <DataTableSkeleton rows={8} columns={5} />
          ) : (
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.student_id}>
                  <TableCell>
                    <div className="font-medium">{row.student_name}</div>
                    <div className="text-muted-foreground text-xs">
                      {row.has_portal_login ? "Portal active" : "No portal login"}
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.has_portal_login ? row.active_plans : "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.has_portal_login ? row.due_cards : "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.has_portal_login ? row.streak_days : "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {row.last_active_at
                      ? formatRelativeTime(row.last_active_at)
                      : row.has_portal_login
                        ? "No activity"
                        : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
      </DataTableScroll>
      {!query.isLoading && items.length === 0 ? (
        <DataTableEmpty
          icon={<GraduationCap aria-hidden="true" />}
          title="No learners yet"
          description="Import learners and send portal invites — progress appears here once they log in."
        />
      ) : null}
      {!query.isLoading && items.length > 0 ? (
        <DataTableFooter>
          <DataTablePagination
            page={page + 1}
            pageSize={pageSize}
            totalRows={total}
            onPageChange={(next) => setPage(Math.max(0, next - 1))}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(0);
            }}
            testIdPrefix="learning-progress"
          />
        </DataTableFooter>
      ) : null}
    </DataTable>
  );
}
